
import { createCustomElement, NgElement, WithProperties } from '@angular/elements';
import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Injector, Input, Output } from "@angular/core";
import { FlexLayoutModule, FlexModule } from "@ngbracket/ngx-layout";
import { SettingLookupOption } from "@tylertech/tyler-api-types";

function isDefined(): boolean
{
    return customElements.get(ProfileCardComponent.customElementSelector) !== undefined;
}

export function defineProfileCardComponent(injector: Injector): void
{
    if (isDefined())
    {
        return;
    }

    const profileCardComponent = createCustomElement(ProfileCardComponent, { injector });
    customElements.define(ProfileCardComponent.customElementSelector, profileCardComponent);
}

export function createProfileCardElement(inputs: ProfileCardInputs): NgElement & WithProperties<ProfileCardComponent>
{
    const customElement = document.createElement(ProfileCardComponent.customElementSelector) as NgElement & WithProperties<ProfileCardComponent>;
    customElement.items = inputs.items;
    customElement.version = inputs.version;
    customElement.passwordExpiring = inputs.passwordExpiring;
    customElement.notifications = inputs.notifications;
    customElement.showERPProAppDownload = inputs.showERPProAppDownload;

    return customElement;
}

export const enum ProfileCardOutput
{
    ChangePassword = "changePassword",
    ViewNotifications = "viewNotifications",
    downloadERPProApp = "downloadERPProApp",
}

type ProfileCardOutputs = "changePassword" | "viewNotifications" | "downloadERPProApp";

type ProfileCardIgnoredProperties =
    | ProfileCardOutputs
    | "onChangePasswordClick" | "onViewClick" | "onDownloadERPProAppClick";
type ProfileCardInputs = Omit<
    Pick<ProfileCardComponent, keyof ProfileCardComponent>,
    ProfileCardIgnoredProperties>;

@Component({
    selector: "profile-card",
    templateUrl: "./profileCard.component.html",
    styleUrls: [
        "./profileCard.component.scss"
    ],
    imports: [
    FlexLayoutModule,
    FlexModule
],
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA
    ]
})
// TODO - Refactor component to accept `Array<Label | Command>` instead of explicitly implementing each item.
export class ProfileCardComponent
{
    /**
     * The selector for registering the angular element as.
     * NOTE: this should be different than the `@Component` selector to avoid any confusion.
     */
    static readonly customElementSelector = "erp-pro-profile-card";

    @Input() items: SettingLookupOption[];
    @Input() version: string;
    @Input() passwordExpiring: boolean;
    @Input() notifications: number;
    @Input() showERPProAppDownload: boolean;

    @Output() readonly changePassword = new EventEmitter<void>();
    @Output() readonly viewNotifications = new EventEmitter<void>();
    @Output() readonly downloadERPProApp = new EventEmitter<void>();

    onChangePasswordClick(): void
    {
        this.changePassword.emit();
    }

    onViewClick(): void
    {
        this.viewNotifications.emit();
    }

    onDownloadERPProAppClick(): void
    {
        this.downloadERPProApp.emit();
    }
}
